import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Pagination, TextField, Paper, Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { blue, grey } from '@mui/material/colors';

import DialogCreateMovimiento from './DialogCreateMovimiento';

import { useAuth } from '../../../Auth/AuthContext';
import MovimientoEmpleadoRowTable from './MovimientoEmpleadoRowTable';
import { authenticatedSessionRequestConfig } from '../../../utils/csrf';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
const API_URL = process.env.REACT_APP_API_URL;

export const deleteMovementBeforeSideEffects = async (
  apiUrl,
  id,
  cashboxId,
  httpClient = axios
) => {
  try {
    const requestConfig = await authenticatedSessionRequestConfig(
      apiUrl,
      (url, config) => httpClient.get(url, config)
    );
    await httpClient.delete(`${apiUrl}/movimiento/${id}/`, {
      ...requestConfig,
      params: { caja_id: cashboxId },
    });
    return null;
  } catch (error) {
    return getApiErrorMessage(error, 'No fue posible eliminar el movimiento.');
  }
};

export const runDeleteWithSuccessEffects = async (deleteRequest, onSuccess) => {
  const errorMessage = await deleteRequest();
  if (errorMessage) return errorMessage;
  await onSuccess();
  return null;
};

export const refreshMovementViews = async (fetchMovements, refreshCashbox) => {
  const operations = [Promise.resolve().then(fetchMovements)];
  if (typeof refreshCashbox === 'function') {
    operations.push(Promise.resolve().then(refreshCashbox));
  }
  const results = await Promise.allSettled(operations);
  results.forEach((result) => {
    if (result.status === 'rejected') {
      console.error('No fue posible refrescar la vista de Caja:', result.reason);
    }
  });
};

// creo que no se quedan los decimales por culpa del Number
// eliminar y q funcione

function formatearFecha(fecha) {
  // Si ya parece estar formateada, la devolvemos como está
  if (typeof fecha === 'string' && fecha.includes('de') && fecha.includes(':')) {
    return fecha;
  }

  const date = new Date(fecha);
  return date.toLocaleString('es-CO', {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

async function actualizarReporteDiarioMIBorrar(valorEliminado, fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  try {
    const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "M-I");

    if (reporteExistente) {
      const nuevoValor = parseFloat(reporteExistente.valor) - Number(valorEliminado);

      await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: nuevoValor });
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

async function actualizarReporteDiarioMVBorrar(fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "M-V");
  if (reporteExistente) {
      await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: Number(reporteExistente.valor) - 1 } );
  }
}

async function actualizarReporteDiarioEIBorrar(valorEliminado, fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  try {
    const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "E-I");

    if (reporteExistente) {
      const nuevoValor = parseFloat(reporteExistente.valor) - parseFloat(valorEliminado);

      const response = await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: nuevoValor });
      console.log(response)
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

async function actualizarReporteDiarioEVBorrar(fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  try {
      const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "E-V");
      if (reporteExistente) {
          const responsePut = await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: Number(reporteExistente.valor) - 1 } );
          console.log(responsePut)
      }
  } catch (error) {
      console.error("Error en la actualización del reporte:", error);
  }
}

function TablaMovimientosEmpleado({ cajaId, handleRefresh }) {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [movimientos, setMovimientos] = useState([]);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletionError, setDeletionError] = useState('');

  const [dialogCreateOpen, setDialogCreateOpen] = useState(false);

  const rowsPerPage = 8;

  const fetchMovimientos = useCallback(async () => {
    if (!cajaId) return;

    try {
      const response = await axios.get(`${API_URL}/movimiento/`, {
        params: {
          caja: cajaId,
          page: currentPage,
          page_size: rowsPerPage,
          search: searchTerm,
        },
      });

      const data = response.data;
      setMovimientos(data.results || []);
      setCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching movimientos:', error);
    }
  }, [cajaId, currentPage, searchTerm]);

  useEffect(() => {
    fetchMovimientos();
  }, [fetchMovimientos]);

  const handleMovementSuccess = useCallback(async () => {
    await refreshMovementViews(fetchMovimientos, handleRefresh);
  }, [fetchMovimientos, handleRefresh]);

  const formatedMovimientos = useMemo(() => {
    return movimientos.map((m) => ({
      ...m,
      fecha: formatearFecha(m.fecha),
    }));
  }, [movimientos]);

  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        setCurrentPage(1);
        setSearchTerm(searchInput);
      }
    },
    [searchInput]
  );

  const handleDeleteMovimiento = useCallback(
    async (id) => {
      const movimientoAEliminar = movimientos.find((m) => m.id === id);

      if (!movimientoAEliminar) return;

      setDeletionError('');
      const guardError = await runDeleteWithSuccessEffects(
        () => deleteMovementBeforeSideEffects(API_URL, id, cajaId),
        async () => {
          // El backend ya ajustó atómicamente la caja y eliminó la M2M.
          if (movimientoAEliminar.isME === 'no') {
            const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Bogota' });
            try {
              const response = await axios.get(`${API_URL}/reporte_diario_cajas/`);
              const reportes = response.data;
              const reporteHoy = reportes.find(
                (r) => r.fecha === hoy && r.tipo.toLowerCase() === movimientoAEliminar.tipo.toLowerCase()
              );

              if (reporteHoy) {
                const nuevoValor = parseFloat(reporteHoy.valor) - Number(movimientoAEliminar.monto);
                await axios.put(`${API_URL}/reporte_diario_cajas/${reporteHoy.id}/`, { valor: nuevoValor });
              }
            } catch (error) {
              console.error('Error en la actualización del reporte:', error);
            }
          } else if (movimientoAEliminar.isME === 'estadia') {
            actualizarReporteDiarioEIBorrar(movimientoAEliminar.monto, movimientoAEliminar.fecha_creacion.split(' ')[0]);
            actualizarReporteDiarioEVBorrar(movimientoAEliminar.fecha_creacion.split(' ')[0]);
          } else if (movimientoAEliminar.isME === 'mensualidad') {
            actualizarReporteDiarioMIBorrar(movimientoAEliminar.monto, movimientoAEliminar.fecha_creacion.split(' ')[0]);
            actualizarReporteDiarioMVBorrar(movimientoAEliminar.fecha_creacion.split(' ')[0]);
          }

          await handleMovementSuccess();
        }
      );
      if (guardError) setDeletionError(guardError);
    },
    [cajaId, handleMovementSuccess, movimientos]
  );

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
  }, []);

  const totalPages = Math.ceil(count / rowsPerPage) || 1;

  return (
    <Box>
      <Paper elevation={3} sx={{ padding: '20px' }}>
        {deletionError && <Alert severity="error" sx={{ mb: 2 }}>{deletionError}</Alert>}
        <TextField
          label="Buscar Movimiento Por Cualquier Dato"
          variant="outlined"
          sx={{ paddingBottom: 2, paddingTop: 0 }}
          fullWidth
          margin="normal"
          value={searchInput}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead
              sx={{
                backgroundColor: isDarkMode ? grey[800] : blue[700],
                color: isDarkMode ? 'black' : 'white',
              }}
            >
              <TableRow>
                <TableCell
                  align="left"
                  sx={{
                    borderLeft: '2px solid #ddd',
                    borderBottom: '2px solid #ddd',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  ID
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    borderLeft: '2px solid #ddd',
                    borderBottom: '2px solid #ddd',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  TIPO
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    borderLeft: '2px solid #ddd',
                    borderBottom: '2px solid #ddd',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  MONTO
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    borderLeft: '2px solid #ddd',
                    borderBottom: '2px solid #ddd',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  FECHA
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    borderLeft: '2px solid #ddd',
                    borderBottom: '2px solid #ddd',
                    borderRight: areUserAdmin ? null : '2px solid #ddd',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  OBSERVACIONES
                  {!areUserAdmin && 
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setDialogCreateOpen(true)}
                      sx={{ minWidth: 150, marginLeft: '32px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 16px' }/* haz que se ponga a la punta de la derecha*/}
                    >
                      Agregar
                    </Button>
                  }
                </TableCell>
                {areUserAdmin && (
                  <TableCell
                    align="left"
                    sx={{
                      borderLeft: '2px solid #ddd',
                      borderBottom: '2px solid #ddd',
                      borderRight: '2px solid #ddd',
                      fontWeight: 'bold',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    ACCIONES
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setDialogCreateOpen(true)}
                      sx={{ minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 16px' }}
                    >
                      Agregar
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {formatedMovimientos.map((item, index) => (
                <MovimientoEmpleadoRowTable
                  key={item.id}
                  idMov={formatedMovimientos.length - index}
                  data={item}
                  areUserAdmin={areUserAdmin}
                  handleDelete={handleDeleteMovimiento}
                  currentCaja={cajaId}
                  onMovementSuccess={handleMovementSuccess}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>

        <DialogCreateMovimiento dialogOpen={dialogCreateOpen} handleDialogClose={() => setDialogCreateOpen(false)} currentCaja={cajaId} onMovementSuccess={handleMovementSuccess}/>
      </Paper>
    </Box>
  );
}

export default TablaMovimientosEmpleado;
